import Evented from "@ember/object/evented"
import Service, { inject as service } from "@ember/service"
import type { PatchModel } from "@shared/models/patch"
import type { TypeToModel } from "@shared/models/type-to-model"
import { resolve } from "rsvp"
import { createInsertContext, type DocumentInsertContext } from "../lib/document-backends/types"
import type Dispatcher from "./dispatcher"
import type InternalEmberStore from "./internal-ember-store"
import type PatchCoordinator from "./patch-coordinator"
import type Finder from "./finder"
import type SocketService from "./socket"

/**
 * The public Ember-facing store: the read/hydration facade for Ember components.
 * Reads come from the internal Ember store; writes go through the `dispatcher`
 * service (see `useDispatch()` for the React equivalent). It has no direct
 * connection to the React side; the two internal stores stay in sync through the
 * document replicator and patch coordinator.
 */
export default class EmberStore extends Service.extend(Evented) {
  @service declare dispatcher: Dispatcher
  @service declare finder: Finder
  @service declare socket: SocketService
  @service("internal-ember-store") declare internalEmberStore: InternalEmberStore
  @service("patch-coordinator") declare patchCoordinator: PatchCoordinator

  constructor(...args) {
    super(...args)

    this.patchCoordinator.on("patch", (patch: PatchModel) => this.trigger("patch", patch))
    this.patchCoordinator.on("foreign-patch", (patch: PatchModel) =>
      this.trigger("foreign-patch", patch)
    )
    this.dispatcher.on("action", (actionName, payload, actionDoc) => {
      this.trigger("action", actionName, payload, actionDoc)
    })
  }

  /**
   * Checks the internal Ember store to see if the desired model is already there, returns if it is,
   * if not, use finder service to ask backend to send requested model here.
   *
   * @param  modelName    [string]
   * @param  id           [string]
   * @param  forceRefresh [boolean - optional]
   * @return              [Promise Object] - as it waits for the finder to access the requested model
   */
  find(modelName: keyof TypeToModel, id: string, forceRefresh?: boolean) {
    let model = this.internalEmberStore.findInMemory(modelName, id)
    setTimeout(() => {
      if (this.isDestroyed || this.isDestroying) return
      this.socket.subscribe(modelName, id)
    }, 1000)
    // if (model) return new Ember.RSVP.Promise((resolve, _reject) => resolve(model))
    if (model && forceRefresh !== true) return resolve(model)
    return this.finder.find(modelName, id)
  }

  /**
   * Checks to see if desired model is in memory
   *
   * @param  modelName [description]
   * @param  id        [description]
   * @return           [object/undefined] requested model, if available
   */
  findInMemory(modelName, id) {
    return this.internalEmberStore.findInMemory(modelName, id)
  }

  /**
   * Resets the in-memory store using the internal Ember store, which resets its store, history, and undo/redo list to empty arrays
   *
   * @return undefined
   */
  clearMemory() {
    // The cross-store clear lives in the patch coordinator, which owns both
    // internal stores. EmberStore stays decoupled from the React side.
    this.patchCoordinator.clearMemory()
  }

  /**
   * Updates existing document in the internal Ember store or makes a new one
   * @param  doc [object] - document object
   * @return     [object] - updated document object if it already exists, otherwise, just the document
   */
  insertDocument(doc, context: DocumentInsertContext = createInsertContext("manual")) {
    this.internalEmberStore.insertDocument(doc, context)
  }
}

declare module "@ember/service" {
  interface Registry {
    "ember-store": EmberStore
  }
}
